import { BackendSchema, RuleSchema } from "layouts/charging-rules/interface/Rules";
import { updateRuleSchema } from "layouts/charging-rules/utils/utils";
import { Action } from "layouts/charging-rules/state/rulesRuducer";
import React from "react";
import { ExportFilterRfid, ComparatorRfidType, RfidInfo } from "types/rfid";
import { RfidStatus } from "layouts/rfid/utils/const";
import { IFormRfidFilter } from "layouts/rfid/rfid-dashboard/filter";
import { SortOrder, SORTORDER } from "types/sort";

export function syncSchemaAfterRfidUpdate(
    updateRfidCardRuleData: BackendSchema | undefined | null,
    schema: typeof RuleSchema,
    setSchema: React.Dispatch<React.SetStateAction<typeof RuleSchema>>,
    ruleSchemaCompany: typeof RuleSchema,
    dispatch: React.ActionDispatch<[action: Action]>
) {
    if (!updateRfidCardRuleData) return;
    try {
        const nextSchema = updateRuleSchema(schema, updateRfidCardRuleData as BackendSchema);
        setSchema(nextSchema);
        const companyBackend: BackendSchema = {
            rules: {
                DOWBlocked: ruleSchemaCompany.DOWBlocked ?? null,
                HoursBlocked: ruleSchemaCompany.HoursBlocked ?? null,
                MaxRechargeDurationOptions: { value: ruleSchemaCompany.MaxRechargeDurationOptions?.value ?? null },
                MaxRechargeEnergyOptions: { value: ruleSchemaCompany.MaxRechargeEnergyOptions?.value ?? null },
            }
        };
        dispatch({ type: "initFromSchema", schema: nextSchema, companyRule: companyBackend });
    } catch (e) {
        // swallow and proceed
        console.error("Failed to sync schema from updateRfidCardRuleData", e);
    }
}

export function buildQueryFromExportFilterRfid(filter_obj: ExportFilterRfid | null): string {
    if (!filter_obj) return "";

    const params = new URLSearchParams();

    if (filter_obj.export) {
        params.set("export", "true");
    }

    const f = filter_obj.filter;
    if (f) {
        if (f.rfidNumber && f.rfidNumber.trim() !== "") {
            params.set("rfidNumber", f.rfidNumber.trim());
        }
        if (f.cardHolder && f.cardHolder.trim() !== "") {
            params.set("cardHolder", f.cardHolder.trim());
        }
        if (f.status && f.status !== "all") {
            params.set("status", f.status);
        }
        if (f.type && f.type !== "ALL") {
            params.set("type", f.type);
        }
    }

    const query = params.toString();
    return query ? `?${query}` : "";
}

export const rfidNumberComparator: ComparatorRfidType = (a: RfidInfo, b: RfidInfo) => {
    const na = Number(a.rfidNumber);
    const nb = Number(b.rfidNumber);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    return a.rfidNumber.localeCompare(b.rfidNumber);
};

export const cardHolderRfidComparator: ComparatorRfidType = (a: RfidInfo, b: RfidInfo) =>
    a.cardHolder.localeCompare(b.cardHolder, undefined, { sensitivity: "base" });

export const statusRfidComparator: ComparatorRfidType = (a: RfidInfo, b: RfidInfo) => {
    const order: Record<RfidStatus, number> = {
        [RfidStatus.Active]: 0,
        [RfidStatus.Deactivate]: 1,
        [RfidStatus.ToBeActivate]: 2,
    };
    return order[a.status] - order[b.status];
};

export function filterRfids(filterValues: IFormRfidFilter) {
    const needleNumber = (filterValues.rfidNumber || "").trim();
    const needleHolder = (filterValues.cardHolder || "").trim().toLowerCase();
    const status = filterValues.status;
    const type = filterValues.type;

    return (rfid: RfidInfo) => {
        if (status !== "all" && rfid.status !== status) return false;
        if (type !== "ALL" && rfid.cardHolderType !== type) return false;
        if (needleNumber && !rfid.rfidNumber.includes(needleNumber)) return false;
        if (needleHolder && !rfid.cardHolder.toLowerCase().includes(needleHolder)) return false;
        return true;
    };
}

export function rfidSort(order: SortOrder, comparator: ComparatorRfidType) {
    return (a: RfidInfo, b: RfidInfo) => {
        const res = comparator(a, b);
        return order === SORTORDER.ASC ? res : -res;
    };
}
